// =============================================================
// BTR Companion — statistiques de profil calculées côté client
// à partir de l'historique (jusqu'à 500 parties) sur une fenêtre choisie.
// =============================================================
import { getChamp } from './ddragon';
import { laneLabel } from './format';

export const RANGES = [
  { id: 20, label: '20 parties' },
  { id: 50, label: '50 parties' },
  { id: 100, label: '100 parties' },
  { id: 'all', label: 'Tout' },
];

export const QUEUE_FR = {
  400: 'Normale', 420: 'Solo/Duo', 430: 'Normale', 440: 'Flexible', 450: 'ARAM', 480: 'Swiftplay', 490: 'Normale',
  700: 'Clash', 720: 'ARAM Clash', 900: 'URF', 1020: 'Un pour tous', 1300: 'Nexus Blitz', 1400: 'Ultimate Spellbook',
  1700: 'Arena', 1710: 'Arena', 1740: 'Arena', 1750: 'Arena', 1760: 'Arena', 1770: 'Arena',
  1810: 'Swarm', 1820: 'Swarm', 1830: 'Swarm', 1840: 'Swarm', 1900: 'URF', 2000: 'Tutoriel', 2010: 'Tutoriel', 2020: 'Tutoriel',
};
const ARENA_IDS = [1700, 1710, 1740, 1750, 1760, 1770];
export const isArena = (m) => ARENA_IDS.includes(m.queueId);
// Parties où sbires, vision et or par minute ont un sens (Faille + ARAM ; pas Arena ni Swarm)
const isFarmMode = (m) => !isArena(m) && !(m.queueId >= 1810 && m.queueId <= 1840);
export const queueLabel = (id) => QUEUE_FR[id] || (id == null ? 'Partie' : 'Autre');
export const QUEUE_GROUPS = [
  { id: 'all', label: 'Toutes' },
  { id: 'ranked', label: 'Classées', ids: [420, 440] },
  { id: 'solo', label: 'Solo/Duo', ids: [420] },
  { id: 'flex', label: 'Flexible', ids: [440] },
  { id: 'normal', label: 'Normales', ids: [400, 430, 480, 490] },
  { id: 'aram', label: 'ARAM', ids: [450, 720] },
  { id: 'arena', label: 'Arena', ids: [1700, 1710, 1740, 1750, 1760, 1770] },
];
export const inQueueGroup = (m, g) => g === 'all' || (QUEUE_GROUPS.find((x) => x.id === g)?.ids || []).includes(m.queueId);

const DAYS = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
const avg = (arr) => (arr.length ? arr.reduce((s, v) => s + v, 0) / arr.length : 0);
const pct = (a, b) => (b ? +((a / b) * 100).toFixed(1) : 0);
const r1 = (v) => +Number(v).toFixed(1);
const r2 = (v) => +Number(v).toFixed(2);

/** Sélection de la fenêtre : `range` = 20|50|100|'all', puis filtre de file. */
export function selectMatches(matches, range = 'all', queueGroup = 'all') {
  const filtered = (matches || []).filter((m) => inQueueGroup(m, queueGroup));
  return range === 'all' ? filtered : filtered.slice(0, Number(range));
}

/** Toutes les statistiques d'un ensemble de parties (les remakes sont écartés). */
export function computeStats(sel) {
  const games = (sel || []).filter((m) => !m.remake);
  const remakes = (sel || []).length - games.length;
  const wins = games.filter((m) => m.win).length;
  const losses = games.length - wins;
  const k = avg(games.map((m) => m.kda[0])), d = avg(games.map((m) => m.kda[1])), a = avg(games.map((m) => m.kda[2]));
  const sumK = games.reduce((s, m) => s + m.kda[0], 0), sumD = games.reduce((s, m) => s + m.kda[1], 0), sumA = games.reduce((s, m) => s + m.kda[2], 0);
  const kda = sumD ? r2((sumK + sumA) / sumD) : r2(sumK + sumA);
  const minutes = games.reduce((s, m) => s + (m.durationSec || 0) / 60, 0);
  const farm = games.filter(isFarmMode);
  const farmMin = farm.reduce((s, m) => s + (m.durationSec || 0) / 60, 0);
  const withKp = games.filter((m) => m.kp != null);
  const withShare = games.filter((m) => m.dmgShare != null);

  // Séries : en cours (depuis la partie la plus récente) et meilleure série de victoires
  let streak = { type: null, n: 0 };
  if (games.length) {
    const t = games[0].win;
    let n = 0; for (const m of games) { if (m.win === t) n += 1; else break; }
    streak = { type: t ? 'win' : 'loss', n };
  }
  let bestWin = 0, cur = 0;
  [...games].reverse().forEach((m) => { cur = m.win ? cur + 1 : 0; if (cur > bestWin) bestWin = cur; });

  // Multi-kills (plus grand multi-kill de chaque partie)
  const multikills = { double: 0, triple: 0, quadra: 0, penta: 0 };
  games.forEach((m) => { if (m.multikill >= 5) multikills.penta += 1; else if (m.multikill === 4) multikills.quadra += 1; else if (m.multikill === 3) multikills.triple += 1; else if (m.multikill === 2) multikills.double += 1; });

  // Par champion
  const byChampMap = new Map();
  games.forEach((m) => {
    const key = m.championId || m.champion;
    const e = byChampMap.get(key) || { championId: m.championId, id: m.champion, games: 0, wins: 0, k: 0, d: 0, a: 0, cs: 0, min: 0, farmMin: 0, dmg: 0, kp: [], share: [] };
    e.games += 1; if (m.win) e.wins += 1;
    e.k += m.kda[0]; e.d += m.kda[1]; e.a += m.kda[2];
    e.min += (m.durationSec || 0) / 60; e.dmg += m.damage || 0;
    if (isFarmMode(m)) { e.cs += m.cs || 0; e.farmMin += (m.durationSec || 0) / 60; }
    if (m.kp != null) e.kp.push(m.kp);
    if (m.dmgShare != null) e.share.push(m.dmgShare);
    byChampMap.set(key, e);
  });
  const byChamp = [...byChampMap.values()].map((e) => {
    const dd = getChamp(Number(e.championId)) || getChamp(e.id) || null;
    return {
      id: dd?.id || e.id, championId: e.championId, name: dd?.name || e.id, title: dd?.title || '',
      games: e.games, wins: e.wins, losses: e.games - e.wins, winrate: pct(e.wins, e.games),
      kda: e.d ? r2((e.k + e.a) / e.d) : r2(e.k + e.a),
      avgK: r1(e.k / e.games), avgD: r1(e.d / e.games), avgA: r1(e.a / e.games),
      csMin: e.farmMin ? r1(e.cs / e.farmMin) : null, dmgMin: e.min ? Math.round(e.dmg / e.min) : 0,
      kp: e.kp.length ? Math.round(avg(e.kp)) : null, dmgShare: e.share.length ? Math.round(avg(e.share)) : null,
    };
  }).sort((x, y) => y.games - x.games || y.winrate - x.winrate);
  const bestChamp = [...byChamp].filter((c) => c.games >= 5).sort((x, y) => y.winrate - x.winrate || y.games - x.games)[0] || null;

  // Par rôle
  const byRoleMap = new Map();
  games.forEach((m) => { if (!m.role || m.role === 'Invalid') return; const e = byRoleMap.get(m.role) || { role: m.role, games: 0, wins: 0 }; e.games += 1; if (m.win) e.wins += 1; byRoleMap.set(m.role, e); });
  const roleTotal = [...byRoleMap.values()].reduce((s, e) => s + e.games, 0) || 1;
  const byRole = [...byRoleMap.values()].sort((x, y) => y.games - x.games)
    .map((e) => ({ role: e.role, label: laneLabel(e.role), games: e.games, wins: e.wins, pct: Math.round((e.games / roleTotal) * 100), winrate: pct(e.wins, e.games) }));

  // Par file
  const byQueueMap = new Map();
  games.forEach((m) => { const q = queueLabel(m.queueId); const e = byQueueMap.get(q) || { queue: q, games: 0, wins: 0 }; e.games += 1; if (m.win) e.wins += 1; byQueueMap.set(q, e); });
  const byQueue = [...byQueueMap.values()].sort((x, y) => y.games - x.games).map((e) => ({ ...e, winrate: pct(e.wins, e.games) }));

  // Jours / heures
  const byDay = DAYS.map((label) => ({ label, games: 0, wins: 0 }));
  const byHour = Array.from({ length: 24 }, (_, h) => ({ hour: h, games: 0, wins: 0 }));
  games.forEach((m) => {
    if (!m.gameCreation) return;
    const dt = new Date(Number(m.gameCreation));
    const dd = byDay[dt.getDay()]; dd.games += 1; if (m.win) dd.wins += 1;
    const hh = byHour[dt.getHours()]; hh.games += 1; if (m.win) hh.wins += 1;
  });
  // Lundi en premier
  const byDayMon = [...byDay.slice(1), byDay[0]].map((x) => ({ ...x, winrate: pct(x.wins, x.games) }));

  // Winrate cumulé (de la plus ancienne à la plus récente)
  const cumul = []; let w = 0;
  [...games].reverse().forEach((m, i) => { if (m.win) w += 1; cumul.push(r1((w / (i + 1)) * 100)); });

  // Durée moyenne selon le résultat
  const durWin = avg(games.filter((m) => m.win).map((m) => m.durationSec || 0));
  const durLoss = avg(games.filter((m) => !m.win).map((m) => m.durationSec || 0));

  const oldest = games.length ? games[games.length - 1].gameCreation : null;
  const newest = games.length ? games[0].gameCreation : null;

  return {
    games: games.length, remakes, wins, losses, winrate: pct(wins, games.length),
    avgK: r1(k), avgD: r1(d), avgA: r1(a), kda,
    csMin: farmMin ? r1(farm.reduce((s, m) => s + (m.cs || 0), 0) / farmMin) : 0,
    dmgMin: minutes ? Math.round(games.reduce((s, m) => s + (m.damage || 0), 0) / minutes) : 0,
    goldMin: farmMin ? Math.round(farm.reduce((s, m) => s + (m.gold || 0), 0) / farmMin) : 0,
    visionMin: farmMin ? r2(farm.reduce((s, m) => s + (m.vision || 0), 0) / farmMin) : 0,
    farmGames: farm.length,
    kp: withKp.length ? Math.round(avg(withKp.map((m) => m.kp))) : null,
    dmgShare: withShare.length ? Math.round(avg(withShare.map((m) => m.dmgShare))) : null,
    avgDuration: minutes && games.length ? Math.round((minutes / games.length) * 60) : 0,
    durWin: Math.round(durWin), durLoss: Math.round(durLoss),
    streak, bestWinStreak: bestWin, multikills,
    byChamp, bestChamp, byRole, byQueue, byDay: byDayMon, byHour, cumul,
    form: games.slice(0, 20).map((m) => m.win),
    oldest, newest,
  };
}

export const fmtMinutes = (sec) => `${Math.floor((sec || 0) / 60)} min`;
export const fmtDateShort = (ms) => (ms ? new Date(Number(ms)).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) : '');
